import type { UserRole } from '@rendimees24/shared-types';
import { useState } from 'react';

import { useAuth } from '../auth/AuthContext';
import { homeForRole } from '../auth/roleHome';
import { signInWithEmail } from '../services/firebase';

/**
 * DEVELOPMENT-ONLY admin role-switcher.
 *
 * A floating widget that lets a developer one-tap sign in as the seeded
 * `dev.admin` / `dev.client` / `dev.worker` accounts to play through each
 * role's screens against real data — without logging out and back in.
 *
 * This is Option D: a REAL email/password sign-in as a seeded test account.
 * There is no custom-token impersonation, no query/callable/rules change —
 * `onAuthStateChanged` + `loadProfile` in AuthContext pick up the new role
 * automatically, so all this component does is call `signInWithEmail` and
 * navigate to that role's home.
 *
 * Environment-gated (available on the DEPLOYED dev web app, not only locally):
 * it renders `null` unless the Firebase project is the dev project
 * (`rendimees24-dev`) — so it appears on the hosted dev build for admins, but a
 * staging/prod build (different project id) never shows it, and those stages'
 * env never carries the `VITE_DEV_*` creds. It is additionally scoped to STAFF
 * (admin/dispatcher/finance) plus the seeded test accounts themselves (so you
 * can switch back after becoming a worker/client) — a logged-out or ordinary
 * worker/client visitor never sees it.
 *
 * SECURITY NOTE: because it must work on the hosted dev bundle, the seeded
 * `dev.*` account passwords are embedded in the dev build's JS (via `VITE_DEV_*`).
 * That is acceptable ONLY because these are throwaway accounts on the throwaway
 * dev project; it is confined there by the project-id gate. If this ever needs
 * to reach a sensitive environment, replace the embedded creds with a dev-only,
 * admin-gated custom-token callable so no passwords ship in the client bundle.
 *
 * Being a dev-only tool, its labels are hardcoded English `[DEV]`-prefixed
 * strings and deliberately NOT added to the i18n catalogs.
 */

interface DevRole {
  key: string;
  /** Button label (kept obviously dev-only). */
  label: string;
  /** Role used to resolve the post-sign-in landing route. */
  role: UserRole;
  email: string | undefined;
  password: string | undefined;
}

const DEV_ROLES: DevRole[] = [
  {
    key: 'admin',
    label: 'Admin',
    role: 'admin',
    email: import.meta.env.VITE_DEV_ADMIN_EMAIL,
    password: import.meta.env.VITE_DEV_ADMIN_PASSWORD,
  },
  {
    key: 'client',
    label: 'Client',
    role: 'client_user',
    email: import.meta.env.VITE_DEV_CLIENT_EMAIL,
    password: import.meta.env.VITE_DEV_CLIENT_PASSWORD,
  },
  {
    key: 'worker',
    label: 'Worker',
    role: 'worker',
    email: import.meta.env.VITE_DEV_WORKER_EMAIL,
    password: import.meta.env.VITE_DEV_WORKER_PASSWORD,
  },
];

/** True only when pointed at the dev Firebase project (hosted or local). */
const PROJECT_IS_DEV = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'rendimees24-dev';

/** The seeded accounts allowed to see the switcher (so they can switch back). */
const SEEDED_EMAILS = [
  import.meta.env.VITE_DEV_ADMIN_EMAIL,
  import.meta.env.VITE_DEV_CLIENT_EMAIL,
  import.meta.env.VITE_DEV_WORKER_EMAIL,
].filter((e): e is string => Boolean(e));

export function RoleSwitcher() {
  const { user } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Folded away on a narrow screen.
   *
   * It is fixed to the bottom-left with the highest z-index there is, which
   * on a 393px phone means a 260px panel sitting on top of whatever the page
   * put there — and swallowing the taps meant for it. Dev tooling is allowed
   * to be visible; it is not allowed to take the screen away from the thing
   * being tested. Tap the badge to open it.
   */
  const [open, setOpen] = useState(typeof window === 'undefined' ? true : window.innerWidth >= 600);

  // Env gate — absent from any non-dev-project build (staging/prod).
  if (!PROJECT_IS_DEV) return null;

  // Only offer roles whose credentials are actually present in the env.
  const available = DEV_ROLES.filter((r) => r.email && r.password);
  if (available.length === 0) return null;

  const currentRole = user?.role ?? null;
  const currentEmail = user?.email ?? null;
  // Scope to staff (who legitimately test roles) + the seeded accounts
  // themselves (so switching back from worker/client stays possible). A
  // logged-out or ordinary worker/client visitor never sees it.
  const isStaff =
    currentRole === 'admin' || currentRole === 'dispatcher' || currentRole === 'finance';
  const isSeededAccount = !!currentEmail && SEEDED_EMAILS.includes(currentEmail);
  if (!isStaff && !isSeededAccount) return null;

  const switchTo = async (r: DevRole) => {
    if (!r.email || !r.password) return;
    setError(null);
    setBusy(r.key);
    try {
      await signInWithEmail(r.email, r.password);
      // Full-page load (NOT an SPA navigate): swapping the Firebase Auth user
      // while the previous user's Firestore onSnapshot listeners are still open
      // corrupts the SDK's watch-stream state and throws
      // "FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state". A hard reload
      // tears every listener down and reinitialises Firestore cleanly for the
      // new user.
      window.location.assign(homeForRole(r.role));
    } catch {
      // Most likely the seed account doesn't exist / password mismatch.
      setError(`${r.label}: account not seeded — run npm run seed:dev`);
      setBusy(null);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="[DEV] role switch"
        style={{
          position: 'fixed',
          bottom: 'calc(8px + var(--worker-navigation-offset, 0px))',
          left: 8,
          zIndex: 2147483647,
          background: '#1e1e1e',
          color: '#ff9800',
          border: '1px solid #ff9800',
          borderRadius: 6,
          padding: '2px 6px',
          font: '11px/1.2 system-ui, sans-serif',
          opacity: 0.85,
        }}
      >
        DEV
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'calc(12px + var(--worker-navigation-offset, 0px))',
        left: 12,
        zIndex: 2147483647,
        background: '#1e1e1e',
        color: '#fff',
        border: '1px solid #ff9800',
        borderRadius: 8,
        padding: '6px 8px',
        font: '12px/1.4 system-ui, sans-serif',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        maxWidth: 260,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <strong style={{ color: '#ff9800' }}>[DEV]</strong>
        <span>role switch</span>
        <span style={{ marginLeft: 'auto', opacity: 0.7 }}>
          {currentRole ? `now: ${currentRole}` : 'signed out'}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="hide the dev role switch"
          style={{
            background: 'transparent',
            color: '#fff',
            border: 0,
            cursor: 'pointer',
            padding: '0 2px',
            font: 'inherit',
            opacity: 0.7,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {available.map((r) => {
          const active = r.role === currentRole;
          return (
            <button
              key={r.key}
              type="button"
              disabled={busy !== null}
              onClick={() => void switchTo(r)}
              style={{
                flex: 1,
                cursor: busy ? 'wait' : 'pointer',
                padding: '4px 6px',
                borderRadius: 6,
                border: active ? '1px solid #ff9800' : '1px solid #555',
                background: active ? '#ff9800' : '#2b2b2b',
                color: active ? '#000' : '#fff',
                fontWeight: active ? 700 : 400,
              }}
            >
              {busy === r.key ? '…' : r.label}
            </button>
          );
        })}
      </div>
      {error ? <div style={{ marginTop: 4, color: '#ff6e6e', fontSize: 11 }}>{error}</div> : null}
    </div>
  );
}
